import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, Download, ExternalLink, History, Loader2, Package, StickyNote, Truck } from "lucide-react";
import adminApi from "../lib/adminApi";
import { AdminImg } from "../components/AdminImg";
import { RepaintPanel } from "../components/RepaintPanel";
import { PrintReadyBadge } from "../components/PrintReadyBadge";
import { EmptySlots } from "../components/PrintProfileCard";
import { PaintWarningsList } from "../components/PaintWarnings";
import { ShopAdminGate } from "../components/ShopAdminGate";
import { Button } from "../components/ui/button";
import { adminBase } from "../lib/adminBase";

const PRINT_STATUSES = ["queued", "printing", "printed"];

function Panel({ title, icon: Icon, children, testid }) {
  return (
    <section data-testid={testid} className="rounded-3xl border border-border bg-card/70 p-5">
      <h2 className="font-heading font-bold text-base md:text-lg flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4 text-accent" />} {title}
      </h2>
      <div className="mt-3 text-sm">{children}</div>
    </section>
  );
}

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex justify-between gap-6 py-1">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}

function money(amount, currency) {
  if (amount == null) return null;
  return `${amount} ${currency || ""}`.trim();
}

const DETAIL_ORDER = ["Size", "Height", "Duck", "Duck ID", "Colours"];
const LINK_KEYS = new Set([]);
const SKIP_ATTRS = new Set(["Gift note", "3MF file", "Filaments", "Duck link"]);

function PrintDetails({ attributes, testid }) {
  const attrs = attributes || {};
  const extra = Object.keys(attrs).filter((k) => !DETAIL_ORDER.includes(k) && attrs[k] && !SKIP_ATTRS.has(k));
  const keys = [...DETAIL_ORDER.filter((k) => attrs[k]), ...extra];
  if (keys.length === 0) return null;
  return (
    <div data-testid={testid} className="rounded-2xl border border-border bg-background/60 p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Print details</p>
      <dl className="space-y-1.5 text-sm">
        {keys.map((k) => (
          <div key={k} className="grid grid-cols-[6.5rem_1fr] gap-2">
            <dt className="text-muted-foreground">{k}</dt>
            <dd className="min-w-0 font-medium">
              {LINK_KEYS.has(k) ? (
                <a href={attrs[k]} target="_blank" rel="noopener noreferrer" className="text-accent underline break-all inline-flex items-start gap-1">
                  {attrs[k]}<ExternalLink className="h-3 w-3 shrink-0 mt-0.5" />
                </a>
              ) : (
                <span className="break-words">{attrs[k]}</span>
              )}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function FilamentSwatches({ palette, testid }) {
  if (!palette || palette.length === 0) return null;
  return (
    <div data-testid={testid}>
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Filaments ({palette.length})</p>
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {palette.map((p, i) => (
          <div key={i} data-testid={`${testid}-item-${i}`} className="flex items-center gap-2 text-sm">
            <span className="h-4 w-4 rounded border border-border shrink-0 cursor-help" style={{ background: p.hex }} title={p.hex} />
            <span className="font-medium">{p.name || "Colour"}</span>
            {p.line && <span className="text-xs text-muted-foreground">· {p.line}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}


function fileWarning(files) {
  if (!files) return null;
  const { frozen, version_ordered: vo, version_current: vc, file_version: fv, version_mismatch } = files;
  if (frozen && version_mismatch) {
    return `Saved file is v${fv}, but the customer ordered v${vo}. Check with the customer before printing.`;
  }
  if (!frozen) {
    return `No saved file — this is the current version (v${vc}).`;
  }
  if (fv !== vc) {
    return `Printing the version ordered (v${vo}); the duck has since changed to v${vc}.`;
  }
  return null;
}

function DetailInner() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewsRev, setViewsRev] = useState(0); // bumped after a repaint so the turntable images reload
  const [error, setError] = useState("");
  const [savingStatus, setSavingStatus] = useState("");
  const [downloading, setDownloading] = useState("");
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await adminApi.get(`/orders/${orderId}`);
      setOrder(data);
      setNotes(data.notes || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load this order.");
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    load();
  }, [load]);

  // One card per duck AND size: the same duck can be ordered at several sizes in one order.
  const lineKey = (d) => `${d.duck_id}@${d.height ?? ""}`;
  const lineUrl = (d, action) => `/orders/${orderId}/ducks/${d.duck_id}/${action}`;
  const lineParams = (d, extra = {}) => ({ ...extra, ...(d.height ? { height: d.height } : {}) });

  const applyLocalStatus = (key, status) =>
    setOrder((o) => ({
      ...o,
      ducks: o.ducks.map((d) =>
        lineKey(d) === key
          ? { ...d, print_status: status, status_history: [...(d.status_history || []), { status, at: new Date().toISOString(), by: "you" }] }
          : d
      ),
    }));

  const updatePrintStatus = async (d, status) => {
    setSavingStatus(lineKey(d));
    try {
      await adminApi.post(lineUrl(d, "print-status"), { status }, { params: lineParams(d) });
      applyLocalStatus(lineKey(d), status);
    } catch {
      /* keep the current status on failure */
    } finally {
      setSavingStatus("");
    }
  };

  const setAllStatus = async (status) => {
    setSavingStatus("__all__");
    try {
      await adminApi.post(`/orders/${orderId}/print-status`, { status });
      setOrder((o) => ({
        ...o,
        ducks: o.ducks.map((d) => ({ ...d, print_status: status, status_history: [...(d.status_history || []), { status, at: new Date().toISOString(), by: "you" }] })),
      }));
    } catch {
      /* keep current statuses on failure */
    } finally {
      setSavingStatus("");
    }
  };

  const downloadFile = async (d, fmt, attributes) => {
    setDownloading(`${lineKey(d)}:${fmt}`);
    try {
      const res = await adminApi.get(lineUrl(d, "file"), { params: lineParams(d, { fmt }), responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/.exec(cd);
      const name = m ? m[1] : `duck.${fmt}`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      // Local file unavailable (e.g. duck lives in another environment) → open the public export link
      const src = attributes?.["3MF file"];
      const pub = src ? src.replace(/fmt=3mf/i, `fmt=${fmt}`) : null;
      if (pub) window.open(pub, "_blank", "noopener");
    } finally {
      setDownloading("");
    }
  };

  const refreeze = async (d) => {
    setDownloading(`${lineKey(d)}:refreeze`);
    try {
      await adminApi.post(lineUrl(d, "refreeze"), null, { params: lineParams(d) });
      await load();
    } catch {
      /* leave the page untouched on failure */
    } finally {
      setDownloading("");
    }
  };

  const repaint = async (d, opts) => {
    const { data } = await adminApi.post(lineUrl(d, "repaint"), { ...opts, refreeze: true }, { params: lineParams(d) });
    setViewsRev((r) => r + 1); // same image URLs before and after a repaint: bump them so the turntable reloads
    load();
    return data;
  };

  const addNote = async () => {
    const text = noteText.trim();
    if (!text) return;
    setSavingNote(true);
    try {
      const { data } = await adminApi.post(`/orders/${orderId}/notes`, { note: text });
      setNotes((prev) => [data, ...prev]);
      setNoteText("");
    } catch {
      /* keep the draft on failure */
    } finally {
      setSavingNote(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen grid place-items-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }
  if (error) {
    return (
      <div data-testid="order-detail-error" className="min-h-screen grid place-items-center px-6 text-center">
        <div>
          <p className="text-rose-600">{error}</p>
          <Link to={`${adminBase()}/orders`} className="text-accent font-semibold hover:underline mt-3 inline-block">← Back to orders</Link>
        </div>
      </div>
    );
  }

  const addr = order.shipping_address || {};
  const addrLines = [addr.name, addr.address1, addr.address2, [addr.city, addr.province, addr.zip].filter(Boolean).join(", "), addr.country].filter(Boolean);

  return (
    <div data-testid="order-detail-page" className="min-h-screen bg-dots">
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <Link to={`${adminBase()}/orders`} data-testid="order-back-link" className="text-sm text-accent font-semibold hover:underline">← Orders</Link>
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold mt-1">{order.order_name}</h1>
            <p className="text-xs text-muted-foreground mt-1">{(order.created_at || "").slice(0, 10)}</p>
          </div>
          <Button asChild className="rounded-full h-11 px-5 font-bold gap-2">
            <a data-testid="open-in-shopify-btn" href={order.admin_url} target="_top" rel="noopener">
              Open in Shopify admin <ExternalLink className="h-4 w-4" />
            </a>
          </Button>
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <Panel title="Customer & shipping" testid="order-customer-panel">
            <Row label="Name" value={order.customer?.name} />
            <Row label="Email" value={order.customer?.email} />
            <Row label="Phone" value={order.customer?.phone} />
            <div data-testid="order-shipping-address" className="mt-3 text-muted-foreground whitespace-pre-line">
              {addrLines.length ? addrLines.join("\n") : "No shipping address on file."}
            </div>
          </Panel>

          <Panel title="Totals" testid="order-totals">
            <Row label="Subtotal" value={money(order.totals?.subtotal, order.totals?.currency)} />
            <Row label="Shipping" value={money(order.totals?.shipping, order.totals?.currency)} />
            <Row label="Tax" value={money(order.totals?.tax, order.totals?.currency)} />
            <div className="border-t border-border mt-2 pt-2">
              <Row label="Total" value={money(order.totals?.total, order.totals?.currency)} />
            </div>
            {order.gift_note && (
              <div data-testid="order-gift-note" className="mt-3 rounded-xl border-l-4 border-accent bg-accent/10 px-3 py-2">
                <span className="font-semibold">Gift note:</span> {order.gift_note}
              </div>
            )}
            {order.note && <div data-testid="order-note" className="mt-2 text-muted-foreground"><span className="font-semibold">Order note:</span> {order.note}</div>}
          </Panel>
        </div>

        <div className="flex items-center justify-between gap-3 mt-8 mb-3 flex-wrap">
          <h2 className="font-heading font-bold text-lg">Ducks</h2>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Set all:</span>
            {PRINT_STATUSES.map((s) => (
              <Button
                key={s}
                data-testid={`mark-all-${s}-btn`}
                variant="outline"
                size="sm"
                className="rounded-full capitalize"
                disabled={savingStatus === "__all__" || !order.ducks?.length}
                onClick={() => setAllStatus(s)}
              >
                {s}
              </Button>
            ))}
          </div>
        </div>
        <div className="grid gap-4">
          {order.ducks.map((d) => {
            const warning = fileWarning(d.files);
            const printable = d.files?.printable !== false;
            const canDownload = printable || !!d.attributes?.["3MF file"];
            return (
              <div key={lineKey(d)} data-testid={`order-duck-${d.duck_id}`} className="rounded-3xl border border-border bg-card/70 p-4">
                <div className="flex gap-4 flex-col sm:flex-row sm:items-start">
                  <div className="shrink-0 w-full sm:w-56 space-y-3">
                    <AdminImg src={d.thumb} fallback={d.public_thumb} alt={d.duck_name || "duck"} className="h-56 w-56 rounded-2xl object-cover bg-muted" />
                    <label className="flex items-center gap-2 text-xs text-muted-foreground">
                      Print status
                      <select
                        data-testid={`duck-print-status-${d.duck_id}`}
                        value={d.print_status}
                        disabled={savingStatus === lineKey(d)}
                        onChange={(e) => updatePrintStatus(d, e.target.value)}
                        className="rounded-full border border-border bg-card h-8 px-3 text-sm capitalize disabled:opacity-60"
                      >
                        {PRINT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        data-testid={`duck-download-3mf-${d.duck_id}`}
                        variant="outline"
                        size="sm"
                        className="rounded-full gap-1.5"
                        disabled={!canDownload || downloading === `${lineKey(d)}:3mf`}
                        onClick={() => downloadFile(d, "3mf", d.attributes)}
                      >
                        {downloading === `${lineKey(d)}:3mf` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} 3MF
                      </Button>
                      <Button
                        data-testid={`duck-download-stl-${d.duck_id}`}
                        variant="outline"
                        size="sm"
                        className="rounded-full gap-1.5"
                        disabled={!canDownload || downloading === `${lineKey(d)}:stl`}
                        onClick={() => downloadFile(d, "stl", d.attributes)}
                      >
                        {downloading === `${lineKey(d)}:stl` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} STL
                      </Button>
                    </div>
                    {d.attributes?.["Duck link"] && (
                      <Button
                        asChild
                        data-testid={`duck-direct-link-${d.duck_id}`}
                        variant="outline"
                        size="sm"
                        className="rounded-full gap-1.5 w-full bg-yellow-400 hover:bg-yellow-500 text-black border-transparent"
                      >
                        <a href={d.attributes["Duck link"]} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-3.5 w-3.5" /> Direct Duck Link
                        </a>
                      </Button>
                    )}
                    {d.files?.frozen && !warning && <span data-testid={`duck-file-exact-${d.duck_id}`} className="block text-xs text-emerald-600">Exact file ordered (v{d.files.version_ordered})</span>}
                    <FilamentSwatches palette={d.palette} testid={`order-duck-filaments-${d.duck_id}`} />
                    {d.empty_slots?.length > 0 && (
                      <p className="text-xs text-muted-foreground"><EmptySlots empty={d.empty_slots} testId={`order-duck-empty-slots-${d.duck_id}`} /></p>
                    )}
                    <PaintWarningsList warnings={d.paint_warnings} testId={`order-duck-paint-warnings-${d.duck_id}`} />
                  </div>
                  <div className="min-w-0 flex-1 space-y-4">
                    {d.story && (d.story.name || d.story.description) && (
                      <div data-testid={`order-duck-story-${d.duck_id}`}>
                        <p className="font-heading font-bold text-base">{d.story.name || d.duck_name}</p>
                        {d.story.description && <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{d.story.description}</p>}
                      </div>
                    )}
                    <PrintDetails attributes={{ ...d.attributes, Colours: d.colors ? String(d.colors) : undefined }} testid={`order-duck-details-${d.duck_id}`} />
                    <div data-testid={`order-duck-turntable-${d.duck_id}`}>
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Print mesh · turntable</p>
                      <div className="mb-2"><PrintReadyBadge creationId={d.duck_id} share notice={d.export_notice} /></div>
                      <div className="flex gap-2 flex-wrap">
                        {d.views.map((v) => (
                          <div key={v.view} className="text-center">
                            <AdminImg src={viewsRev ? `${v.url}&r=${viewsRev}` : v.url} fallback={v.public} alt={`${d.duck_name} ${v.view}`} className="h-24 w-24 rounded-xl object-cover bg-muted" />
                            <p className="text-[10px] text-muted-foreground mt-1 capitalize">{v.view}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                    {d.paint_attention && (
                      <div data-testid={`order-duck-paint-attention-${d.duck_id}`} className="rounded-xl border border-amber-500/60 bg-amber-500/10 px-3 py-2 flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <p className="text-xs font-bold">Painting needs attention{d.paint_attention_source === "fidelity" ? " · colour fidelity" : ""}</p>
                          <p className="text-xs text-muted-foreground">{d.paint_attention_reason || "The colours could not be trusted."}</p>
                        </div>
                      </div>
                    )}
                    {d.palette?.length > 0 && (
                      <RepaintPanel palette={d.palette} paintOpts={d.paint_opts} capacity={d.slot_capacity} admin onRepaint={(opts) => repaint(d, opts)}
                        loadReread={() => adminApi.get(lineUrl(d, "reread-colours")).then((r) => r.data)} />
                    )}
                  </div>
                </div>

                {warning && (
                  <div data-testid={`duck-file-warning-${d.duck_id}`} className="mt-2 flex items-start gap-2 rounded-xl border-l-4 border-amber-400 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> <span>{warning}</span>
                  </div>
                )}
                {!canDownload && (
                  <p data-testid={`duck-not-printable-${d.duck_id}`} className="mt-2 text-xs text-muted-foreground">No printable file yet — this duck has not finished building.</p>
                )}

                {(d.files?.status === "failed" || d.files?.status === "gave_up") && (
                  <div data-testid={`duck-file-error-${d.duck_id}`} className="mt-2 rounded-xl border-l-4 border-rose-400 bg-rose-50 px-3 py-2 text-xs text-rose-800">
                    <p className="flex items-center gap-2 font-semibold">
                      <AlertTriangle className="h-4 w-4 shrink-0" />
                      Print file failed{d.files.status === "gave_up" ? " — gave up after 5 tries" : ""}.
                    </p>
                    {d.files.error && <p className="mt-1 opacity-80 break-words">{d.files.error}</p>}
                    <Button
                      data-testid={`duck-refreeze-${d.duck_id}`}
                      size="sm"
                      variant="outline"
                      className="rounded-full mt-2 gap-1.5"
                      disabled={downloading === `${lineKey(d)}:refreeze`}
                      onClick={() => refreeze(d)}
                    >
                      {downloading === `${lineKey(d)}:refreeze` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Try again"}
                    </Button>
                  </div>
                )}

                {d.status_history?.length > 0 && (
                  <details data-testid={`duck-status-history-${d.duck_id}`} className="mt-3 text-xs">
                    <summary className="cursor-pointer text-muted-foreground flex items-center gap-1.5 select-none">
                      <History className="h-3.5 w-3.5" /> Print status history ({d.status_history.length})
                    </summary>
                    <ul className="mt-2 space-y-1 pl-1">
                      {[...d.status_history].reverse().map((h, i) => (
                        <li key={i} className="flex items-center gap-2 text-muted-foreground">
                          <span className="capitalize font-medium text-foreground">{h.status}</span>
                          <span>· {(h.at || "").replace("T", " ").slice(0, 16)}</span>
                          <span data-testid={`history-by-${h.by || "staff"}`} className={h.by === "shopify-sync" ? "text-emerald-700 font-semibold" : ""}>
                            · {h.by === "shopify-sync" ? `Shopify ${h.shopify_status || "shipped"}` : h.by === "you" ? "you" : `staff ${h.by || ""}`}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-8">
          <Panel title="Staff notes" icon={StickyNote} testid="order-notes-panel">
            <div className="flex flex-col sm:flex-row gap-2">
              <textarea
                data-testid="note-input"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Add an internal note for this order (only staff can see it)…"
                rows={2}
                className="flex-1 rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:ring-2 ring-accent/40 resize-y"
              />
              <Button
                data-testid="save-note-btn"
                className="rounded-full h-10 px-5 font-bold self-start"
                disabled={savingNote || !noteText.trim()}
                onClick={addNote}
              >
                {savingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save note"}
              </Button>
            </div>
            {notes.length === 0 ? (
              <p data-testid="notes-empty" className="mt-3 text-muted-foreground">No notes yet.</p>
            ) : (
              <ul className="mt-4 space-y-3">
                {notes.map((n, i) => (
                  <li key={i} data-testid={`note-${i}`} className="rounded-xl border border-border bg-background/50 px-3 py-2">
                    <p className="whitespace-pre-line">{n.note}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{n.staff_id} · {(n.at || "").replace("T", " ").slice(0, 16)}</p>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>

        <div className="mt-6">
          <Panel title="Shipment (managed in Shopify)" icon={Truck} testid="order-shipment-panel">
            {order.fulfillments?.length ? (
              <ul className="divide-y divide-border/60">
                {order.fulfillments.map((f, i) => (
                  <li key={i} data-testid={`order-fulfillment-${i}`} className="py-3 flex items-start justify-between gap-4">
                    <div>
                      <p className="font-semibold flex items-center gap-2"><Package className="h-4 w-4 text-accent" /> {f.display_status || f.status}</p>
                      <p className="text-xs text-muted-foreground mt-1">{(f.created_at || "").slice(0, 10)}{f.company ? ` · ${f.company}` : ""}</p>
                    </div>
                    {f.url ? (
                      <a data-testid={`order-tracking-${i}`} href={f.url} target="_top" rel="noopener" className="text-accent font-semibold hover:underline whitespace-nowrap">
                        {f.number || "Track"} <ExternalLink className="inline h-3 w-3" />
                      </a>
                    ) : f.number ? (
                      <span className="text-muted-foreground">{f.number}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p data-testid="order-not-shipped" className="text-muted-foreground">Not shipped yet. Fulfil this order in Shopify admin.</p>
            )}
          </Panel>
        </div>
      </main>
    </div>
  );
}

export default function ShopAdminOrderDetail() {
  return (
    <ShopAdminGate>
      <DetailInner />
    </ShopAdminGate>
  );
}
